import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Plus,
  Calendar,
  BarChart3,
  Sparkles,
  Clock,
  CheckCircle,
  AlertCircle,
  FileText,
  Search,
  MoreHorizontal,
  Trash2,
  Eye,
  Edit,
  RefreshCw,
  Users,
  Heart,
  Radio,
  MessageCircle,
  Send,
  Share2,
  LayoutGrid,
  Menu as MenuIcon,
  TrendingUp,
  Rocket
} from 'lucide-react';
import toast from 'react-hot-toast';
import Button from '../../components/ui/Button';
import {
  ContentItem,
  ContentDashboardStats,
  SocialPlatform,
  PLATFORM_CONFIGS,
  ContentStatus
} from '../../types/contentHub';
import { contentHubService } from '../../services/contentHubService';
import { useProject } from '../../contexts/ProjectContext';
import socialService, {
  SocialPost,
  SocialStats,
  SocialAccount,
  AISuggestion
} from '../../services/socialService';

// Import social components
import AISuggestions from '../../components/social/AISuggestions';
import AdvancedComposer from '../../components/social/AdvancedComposer';
import ContentCalendar from '../../components/social/ContentCalendar';
import SocialAnalytics from '../../components/social/SocialAnalytics';
import AccountsManager from '../../components/social/AccountsManager';
import SocialListening from '../../components/social/SocialListening';
import ThreadsReplies from '../../components/social/ThreadsReplies';
import PostMetricsCard from '../../components/content/PostMetricsCard';
import BoostPostModal from '../../components/social/BoostPostModal';

// Tab configuration
type TabType = 'dashboard' | 'suggestions' | 'posts' | 'published' | 'calendar' | 'analytics' | 'listening' | 'accounts' | 'threads_replies';

interface TabConfig {
  id: TabType;
  label: string;
  shortLabel: string;
  icon: React.ComponentType<{ className?: string }>;
  highlight?: boolean;
}

// Tabs configuration will be inside component to use translation

// Quick Stat Card Component
const QuickStatCard: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: number | string;
  change?: number;
  color: string;
}> = ({ icon, label, value, change, color }) => (
  <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
    <div className="flex items-center gap-3">
      <div className={`p-2 rounded-lg ${color}`}>
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{label}</p>
        <div className="flex items-baseline gap-2">
          <p className="text-lg font-bold text-gray-900 dark:text-white">
            {typeof value === 'number' ? value.toLocaleString() : value}
          </p>
          {change !== undefined && change !== 0 && (
            <span className={`text-xs font-medium ${change > 0 ? 'text-green-600' : 'text-red-600'}`}>
              {change > 0 ? '+' : ''}{change}%
            </span>
          )}
        </div>
      </div>
    </div>
  </div>
);

// Platform display meta — short label, background color, link pattern.
// Kept local so the card doesn't have to depend on the huge platform config.
const PLATFORM_META: Record<string, { label: string; bg: string }> = {
  instagram: { label: 'IG', bg: 'bg-gradient-to-br from-purple-500 to-pink-500' },
  instagram_reels: { label: 'Reels', bg: 'bg-gradient-to-br from-purple-600 to-pink-600' },
  facebook: { label: 'FB', bg: 'bg-blue-600' },
  twitter: { label: 'X', bg: 'bg-gray-900 dark:bg-gray-700' },
  linkedin: { label: 'in', bg: 'bg-blue-700' },
  youtube: { label: 'YT', bg: 'bg-red-600' },
  youtube_shorts: { label: 'Sh', bg: 'bg-red-500' },
  tiktok: { label: 'TT', bg: 'bg-gray-900 dark:bg-gray-700' },
  threads: { label: '@', bg: 'bg-gray-900 dark:bg-gray-700' }
};

type PlatformStatus = 'published' | 'failed' | 'processing' | 'scheduled' | 'draft' | 'pending';

const derivePlatformStatus = (
  post: SocialPost,
  platform: string
): { status: PlatformStatus; error?: string; externalId?: string; url?: string } => {
  const externalId = (post.external_ids as any)?.[platform];
  if (externalId) {
    const successEntry = post.publish_results?.success?.find?.((s: any) => s.platform === platform);
    return { status: 'published', externalId, url: successEntry?.url };
  }
  const failed = post.publish_results?.failed?.find?.((f: any) => f.platform === platform);
  if (failed) return { status: 'failed', error: failed.error };
  if (post.status === 'processing') return { status: 'processing' };
  if (post.status === 'scheduled') return { status: 'scheduled' };
  if (post.status === 'draft') return { status: 'draft' };
  return { status: 'pending' };
};

// Post Card Component
const PostCard: React.FC<{
  post: SocialPost;
  onEdit: () => void;
  onDelete: () => void;
  onPublish: () => void;
  onBoost?: () => void;
  onRetryPlatforms?: (platforms: string[]) => void;
  /** True when the user has at least one connected facebook_ads account.
   *  Boost is gated on this — no ads account means no rocket icon at all,
   *  since boosting without ads credentials always fails. */
  canBoost?: boolean;
}> = ({ post, onEdit, onDelete, onPublish, onBoost, onRetryPlatforms, canBoost }) => {
  const { t } = useTranslation();
  const [imageError, setImageError] = useState(false);

  // Ensure media_urls is always an array (handles JSON string or null)
  const mediaUrls = React.useMemo(() => {
    if (!post.media_urls) return [];
    if (Array.isArray(post.media_urls)) return post.media_urls;
    if (typeof post.media_urls === 'string') {
      try {
        const parsed = JSON.parse(post.media_urls);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        // Single URL string
        return post.media_urls ? [post.media_urls] : [];
      }
    }
    return [];
  }, [post.media_urls]);

  // Get the first valid image URL (skip videos)
  const videoExtensions = ['.mp4', '.webm', '.mov', '.avi', '.mkv', '/video/', '/videos/'];
  const imageUrl = mediaUrls.find((url: string) => {
    if (!url || !url.trim()) return false;
    const lowerUrl = url.toLowerCase();
    // Skip video URLs
    return !videoExtensions.some(ext => lowerUrl.includes(ext));
  });

  // If no image found, try to use video thumbnail or first frame
  const thumbnailUrl = imageUrl || mediaUrls.find((url: string) => url && url.trim().length > 0);
  const hasValidImage = imageUrl && !imageError;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 hover:shadow-md transition-shadow">
      <div className="flex gap-4">
        {hasValidImage && (
          <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100 dark:bg-gray-700">
            <img
              src={imageUrl}
              alt=""
              className="w-full h-full object-cover"
              onError={() => setImageError(true)}
            />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                  post.status === 'published' ? 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300' :
                  post.status === 'scheduled' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300' :
                  post.status === 'failed' ? 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300' :
                  post.status === 'partial' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300' :
                  'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                }`}>
                  {t(`social.status.${post.status}`, post.status)}
                </span>
                <div className="flex items-center gap-1 flex-wrap">
                  {post.platforms.map(platform => {
                    const meta = PLATFORM_META[platform] || { label: platform.slice(0, 2).toUpperCase(), bg: 'bg-gray-500' };
                    const state = derivePlatformStatus(post, platform);
                    const ringColor =
                      state.status === 'published' ? 'ring-green-500' :
                      state.status === 'failed' ? 'ring-red-500' :
                      state.status === 'processing' ? 'ring-yellow-400' :
                      state.status === 'scheduled' ? 'ring-blue-400' :
                      'ring-gray-300 dark:ring-gray-600';
                    const statusDotBg =
                      state.status === 'published' ? 'bg-green-500' :
                      state.status === 'failed' ? 'bg-red-500' :
                      state.status === 'processing' ? 'bg-yellow-400' :
                      state.status === 'scheduled' ? 'bg-blue-500' :
                      'bg-gray-400';
                    const tooltip =
                      state.status === 'published' ? `${platform} · ${t('social.status.published', 'Publicado')}` :
                      state.status === 'failed' ? `${platform} · ${t('social.status.failed', 'Falhou')}: ${state.error || ''}` :
                      state.status === 'processing' ? `${platform} · ${t('social.status.processing', 'Processando')}` :
                      state.status === 'scheduled' ? `${platform} · ${t('social.status.scheduled', 'Agendado')}` :
                      `${platform} · ${t(`social.status.${post.status}`, post.status)}`;

                    const chipInner = (
                      <div className={`relative w-7 h-7 rounded-lg flex items-center justify-center text-[10px] font-bold text-white ${meta.bg} ring-2 ${ringColor} transition`}>
                        {meta.label}
                        <span className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full ${statusDotBg} ring-2 ring-white dark:ring-gray-800`} />
                      </div>
                    );

                    if (state.status === 'published' && state.url) {
                      return (
                        <a
                          key={platform}
                          href={state.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={tooltip}
                          className="hover:scale-105 transition-transform"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {chipInner}
                        </a>
                      );
                    }
                    if (state.status === 'failed' && onRetryPlatforms) {
                      return (
                        <button
                          key={platform}
                          type="button"
                          onClick={(e) => { e.stopPropagation(); onRetryPlatforms([platform]); }}
                          title={`${tooltip} · ${t('social.clickToRetry', 'clique pra tentar novamente')}`}
                          className="hover:scale-110 transition-transform cursor-pointer"
                        >
                          {chipInner}
                        </button>
                      );
                    }
                    return (
                      <div key={platform} title={tooltip}>
                        {chipInner}
                      </div>
                    );
                  })}
                </div>
              </div>
              <p className="text-gray-900 dark:text-white line-clamp-2 text-sm">{post.content}</p>
              {post.scheduled_at && post.status === 'scheduled' && (
                <span className="flex items-center gap-1 mt-2 text-xs text-gray-500 dark:text-gray-400">
                  <Clock className="w-3 h-3" />
                  {new Date(post.scheduled_at).toLocaleDateString(undefined, {
                    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit'
                  })}
                </span>
              )}
              {post.status === 'published' && (() => {
                // Show the publish time when we have a valid one; otherwise still
                // render the green "Publicado" marker (a partial/legacy/retried
                // post can be 'published' with a null or malformed published_at —
                // rendering nothing would hide that it went live).
                const d = post.published_at ? new Date(post.published_at) : null;
                const valid = d && !isNaN(d.getTime());
                return (
                  <span className="flex items-center gap-1 mt-2 text-xs text-green-600 dark:text-green-400">
                    <CheckCircle className="w-3 h-3" />
                    {valid
                      ? t('social.publishedAt', 'Publicado em {{date}}', {
                          date: d!.toLocaleDateString(undefined, {
                            day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit'
                          })
                        })
                      : t('social.status.published', 'Publicado')}
                  </span>
                );
              })()}
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              {(post.status === 'draft' || post.status === 'scheduled') && (
                <button onClick={onPublish} className="p-2 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg" title={t('social.publishNow', 'Publish Now')}>
                  <Send className="w-4 h-4" />
                </button>
              )}
              {post.status === 'published' && onBoost && canBoost && (
                <button
                  onClick={onBoost}
                  className={`p-2 rounded-lg ${
                    post.boost_status === 'active' ? 'text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20' :
                    post.boost_status === 'paused' ? 'text-orange-500 hover:bg-orange-50 dark:hover:bg-orange-900/20' :
                    'text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20'
                  }`}
                  title={post.boost_status === 'active' ? 'Boost ativo' : post.boost_status === 'paused' ? 'Boost pausado' : 'Impulsionar'}
                >
                  <Rocket className="w-4 h-4" />
                </button>
              )}
              <button onClick={onEdit} className="p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
                <Edit className="w-4 h-4" />
              </button>
              <button onClick={onDelete} className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>

          {(post.status === 'failed' || post.status === 'partial') && (
            <div className="mt-3 pt-3 border-t border-red-100 dark:border-red-900/30">
              {post.error_message && (
                <p className="text-xs text-red-600 dark:text-red-400 mb-1">{post.error_message}</p>
              )}
              {post.publish_results?.failed && Array.isArray(post.publish_results.failed) && post.publish_results.failed.length > 0 && (
                <div className="space-y-1">
                  {post.publish_results.failed.map((f: { platform: string; error: string }, i: number) => (
                    <p key={i} className="text-xs text-red-500 dark:text-red-400">
                      <span className="font-medium capitalize">{f.platform}:</span> {f.error}
                    </p>
                  ))}
                </div>
              )}
              {post.publish_results?.success && Array.isArray(post.publish_results.success) && post.publish_results.success.length > 0 && (
                <div className="mt-1">
                  {post.publish_results.success.map((s: { platform: string }, i: number) => (
                    <p key={i} className="text-xs text-green-600 dark:text-green-400">
                      <span className="font-medium capitalize">{s.platform}:</span> {t('common.success', 'OK')}
                    </p>
                  ))}
                </div>
              )}
              {(() => {
                const failedPlats = (post.publish_results?.failed || [])
                  .map((f: any) => f.platform)
                  .filter(Boolean);
                if (failedPlats.length > 0 && onRetryPlatforms) {
                  return (
                    <button
                      onClick={() => onRetryPlatforms(failedPlats)}
                      className="mt-2 text-xs text-red-600 dark:text-red-400 hover:underline font-medium"
                    >
                      {t('social.retryFailed', 'Tentar novamente ({{list}})', { list: failedPlats.join(', ') })}
                    </button>
                  );
                }
                return (
                  <button onClick={onPublish} className="mt-2 text-xs text-red-600 dark:text-red-400 hover:underline font-medium">
                    {t('social.retryPublish', 'Tentar novamente')}
                  </button>
                );
              })()}
            </div>
          )}

          {post.status === 'published' && post.metrics && (
            <div className="flex items-center gap-4 mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
              <span className="flex items-center gap-1 text-xs text-gray-600 dark:text-gray-300">
                <Heart className="w-3 h-3 text-pink-500" />
                {post.metrics.likes?.toLocaleString() || 0}
              </span>
              <span className="text-xs font-medium text-primary-600 dark:text-primary-400">
                {post.metrics.engagement_rate || 0}% {t('social.engagement', 'engagement')}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ContentHubPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentProject } = useProject();

  // Get translated tabs
  const tabs: TabConfig[] = [
    { id: 'dashboard', label: t('social.tabs.dashboard', 'Dashboard'), shortLabel: t('social.tabsShort.dashboard', 'Home'), icon: LayoutGrid },
    { id: 'suggestions', label: t('social.tabs.suggestions', 'AI Suggestions'), shortLabel: t('social.tabsShort.suggestions', 'AI'), icon: Sparkles, highlight: true },
    { id: 'posts', label: t('social.tabs.posts', 'My Posts'), shortLabel: t('social.tabsShort.posts', 'Posts'), icon: FileText },
    { id: 'published', label: t('socialMetrics.published', 'Published'), shortLabel: t('socialMetrics.published', 'Published'), icon: TrendingUp },
    { id: 'calendar', label: t('social.tabs.calendar', 'Calendar'), shortLabel: t('social.tabsShort.calendar', 'Calendar'), icon: Calendar },
    { id: 'analytics', label: t('social.tabs.analytics', 'Analytics'), shortLabel: t('social.tabsShort.analytics', 'Stats'), icon: BarChart3 },
    { id: 'listening', label: t('social.tabs.listening', 'Listening'), shortLabel: t('social.tabsShort.listening', 'Social'), icon: Radio },
    { id: 'threads_replies', label: t('social.tabs.socialReplies', 'Social Replies'), shortLabel: t('social.tabsShort.socialReplies', 'Replies'), icon: MessageCircle },
    { id: 'accounts', label: t('social.tabs.accounts', 'Accounts'), shortLabel: t('social.tabsShort.accounts', 'Accounts'), icon: Users }
  ];

  // Main state
  // /content/calendar e /content/all (links antigos e do menu) já abrem na aba certa
  const { pathname } = useLocation();
  const [activeTab, setActiveTab] = useState<TabType>(
    pathname === '/content/calendar' ? 'calendar' : pathname === '/content/all' ? 'posts' : 'dashboard'
  );
  const activeTabRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    activeTabRef.current?.scrollIntoView({ block: 'nearest', inline: 'center' });
  }, [activeTab]);
  const [isLoading, setIsLoading] = useState(true);
  const [isGeneratingSuggestions, setIsGeneratingSuggestions] = useState(false);
  const [showComposer, setShowComposer] = useState(false);
  const [editingPost, setEditingPost] = useState<SocialPost | null>(null);
  const [boostingPost, setBoostingPost] = useState<SocialPost | null>(null);
  const [showBoostModal, setShowBoostModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  // Data state
  const [stats, setStats] = useState<ContentDashboardStats | null>(null);
  const [socialStats, setSocialStats] = useState<SocialStats>({
    totalFollowers: 0,
    followersGrowth: 0,
    totalPosts: 0,
    scheduledPosts: 0,
    avgEngagement: 0
  });
  const [posts, setPosts] = useState<SocialPost[]>([]);
  const [accounts, setAccounts] = useState<SocialAccount[]>([]);
  const [suggestions, setSuggestions] = useState<AISuggestion[]>([]);
  const [analyticsData, setAnalyticsData] = useState<{
    platformMetrics: any[];
    topPosts: any[];
    overview: any;
  } | null>(null);

  // External posts state (posts fetched directly from social networks)
  const [externalPosts, setExternalPosts] = useState<Array<{
    id: string;
    external_id: string;
    content: string;
    platforms: string[];
    published_at: string;
    media_urls?: string[];
    media_type?: string;
    permalink?: string;
    external_ids?: Record<string, string>;
    platform: string;
    content_type?: string;
    account_id: string;
    account_username: string;
    account_profile_image?: string;
    metrics: {
      likes: number;
      comments: number;
      shares: number;
      saves: number;
      reach: number;
      impressions: number;
      engagement_rate: number;
      story?: { views: number; reach: number; replies: number; exits: number; taps_forward: number; taps_back: number };
    };
  }>>([]);
  const [externalAccountsInfo, setExternalAccountsInfo] = useState<Array<{
    id: string;
    platform: string;
    username: string;
    profile_image?: string;
    posts_fetched: number;
    error?: string;
  }>>([]);
  const [isFetchingExternal, setIsFetchingExternal] = useState(false);
  const [selectedAccountFilter, setSelectedAccountFilter] = useState<string | null>(null);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Fetch external posts directly from connected social accounts
  const fetchExternalPosts = useCallback(async () => {
    setIsFetchingExternal(true);
    try {
      // Import polling service
      const { pollJobUntilComplete } = await import('../../services/aiJobService');

      const response = await socialService.fetchExternalPosts({ limit: 30, async: true });

      let result = null;

      // If async mode, poll for result
      if (response.async && response.jobId) {
        result = await pollJobUntilComplete(
          response.jobId,
          undefined,
          2000,
          120000 // 2 minute timeout
        );
      } else if (response.success && response.data) {
        result = response.data;
      }

      if (result) {
        setExternalPosts(result.posts || []);
        setExternalAccountsInfo(result.accounts || []);
      }
    } catch (error) {
      console.error('Error fetching external posts:', error);
    } finally {
      setIsFetchingExternal(false);
    }
  }, []);

  // Fetch all data (analytics loads independently to not block the page)
  const fetchData = useCallback(async () => {
    setIsLoading(true);
    // Limpa dados do projeto anterior antes do fetch — ao trocar de projeto, não
    // exibe contas/posts/sugestões de outro projeto enquanto o novo carrega.
    setAccounts([]);
    setPosts([]);
    setSuggestions([]);
    setAnalyticsData(null);
    try {
      const results = await Promise.allSettled([
        contentHubService.getDashboardStats(),
        // Limit alto pra calendário não perder posts antigos (failed/partial)
        socialService.getPosts({ limit: 500 }),
        socialService.getStats(),
        // Include ads accounts so the "Boost" button can be gated by a real
        // facebook_ads connection — without this the platform filter in
        // getAccounts() strips them out and the boost button would always hide.
        socialService.getAccountsWithAds(),
        socialService.getSuggestions(),
      ]);

      // Handle results safely
      if (results[0].status === 'fulfilled') {
        setStats(results[0].value);
      }
      if (results[1].status === 'fulfilled' && results[1].value.success) {
        setPosts(Array.isArray(results[1].value.data) ? results[1].value.data : []);
      }
      if (results[2].status === 'fulfilled' && results[2].value.success && results[2].value.data) {
        setSocialStats(results[2].value.data);
      }
      if (results[3].status === 'fulfilled' && results[3].value.success) {
        setAccounts(results[3].value.data || []);
      }
      if (results[4].status === 'fulfilled' && results[4].value.success) {
        setSuggestions(results[4].value.data || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setIsLoading(false);
    }

    // Analytics loads in background - doesn't block page render
    contentHubService.getAnalytics('30d')
      .then((data) => setAnalyticsData(data))
      .catch((error) => console.error('Error fetching analytics:', error));
    // Depende do projeto atual: ao trocar de projeto, refaz o fetch (sem depender de
    // um reload de página) — garante que a tela reflita SEMPRE o projeto ativo.
  }, [currentProject?.id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // NOTE: não há "resume de geração" ao recarregar. A geração roda no worker e
  // persiste no banco; ao recarregar, o fetchData traz as sugestões já criadas.
  // (O resume anterior era morto — a flag nunca era gravada — e, se disparasse,
  // chamava handleGenerateSuggestions() sem plataformas, gerando erro/duplicação.)

  // Post handlers
  const handleCreatePost = async (data: any) => {
    setIsSubmitting(true);
    try {
      const shouldPublishNow = data.status === 'publish_now';
      const saveData = { ...data, status: shouldPublishNow ? 'scheduled' : data.status };

      let postId: string;
      if (editingPost && editingPost.id) {
        await socialService.updatePost(editingPost.id, saveData);
        postId = editingPost.id;
        if (!shouldPublishNow) {
          toast.success(data.status === 'scheduled' && data.scheduled_at
            ? t('social.postScheduledAt', 'Agendado para {{when}}', { when: new Date(data.scheduled_at).toLocaleString() })
            : t('messages.updatedSuccessfully', 'Updated successfully'));
        }
      } else {
        const created = await socialService.createPost(saveData);
        postId = created.data?.id;
        if (!shouldPublishNow) {
          toast.success(
            data.status === 'draft'
              ? t('social.draftSaved', 'Draft saved')
              : (data.scheduled_at
                  ? t('social.postScheduledAt', 'Agendado para {{when}}', { when: new Date(data.scheduled_at).toLocaleString() })
                  : t('social.postScheduled', 'Post scheduled'))
          );
        }
      }

      if (shouldPublishNow && postId) {
        await handlePublishPost(postId);
      }

      setShowComposer(false);
      setEditingPost(null);
      fetchData();
    } catch (error: any) {
      console.error('Error creating/updating post:', error);
      // Surface the backend's specific message (e.g. "Plataforma inválida",
      // "Não é possível agendar para uma data no passado") instead of a generic one.
      toast.error(error?.response?.data?.message || t('errors.generic', 'Error saving post'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeletePost = async (id: string) => {
    if (!confirm(t('messages.confirmDelete', 'Are you sure you want to delete?'))) return;
    try {
      await socialService.deletePost(id);
      toast.success(t('social.postDeleted', 'Post deleted'));
      fetchData();
    } catch (error) {
      console.error('Error deleting post:', error);
      toast.error(t('errors.generic', 'Error deleting post'));
    }
  };

  const handlePublishPost = async (id: string) => {
    try {
      toast.loading(t('social.publishing', 'Publishing...'), { id: 'publishing' });

      const response = await socialService.publishPost(id);

      // Async path: poll the job and surface per-platform success/failure so
      // the user doesn't see a green toast when Instagram actually failed.
      if (response.async && response.jobId) {
        const { pollJobUntilComplete } = await import('../../services/aiJobService');

        const pollResult: any = await pollJobUntilComplete(
          response.jobId,
          undefined,
          2000,
          180000 // 3 minute timeout for publishing
        );

        toast.dismiss('publishing');

        // The worker returns { success: [...], failed: [...] }
        const payload = pollResult?.data ?? pollResult ?? {};
        const successes = Array.isArray(payload.success) ? payload.success : [];
        const failures = Array.isArray(payload.failed) ? payload.failed : [];

        if (failures.length === 0 && successes.length > 0) {
          toast.success(t('social.postPublished', 'Post published'));
        } else if (successes.length > 0 && failures.length > 0) {
          const failedList = failures.map((f: any) => `${f.platform}: ${f.error}`).join(' · ');
          toast.error(
            t('social.publishPartial', 'Publicado parcialmente. Falhou em: {{list}}', { list: failedList }),
            { duration: 8000 }
          );
        } else if (failures.length > 0) {
          const failedList = failures.map((f: any) => `${f.platform}: ${f.error}`).join(' · ');
          toast.error(
            t('social.publishFailed', 'Falha ao publicar: {{list}}', { list: failedList }),
            { duration: 8000 }
          );
        } else {
          // No platforms at all — probably no platforms selected on the post
          toast.error(t('social.publishNoPlatforms', 'Nenhuma plataforma elegível para publicar.'), { duration: 6000 });
        }
      } else {
        toast.dismiss('publishing');
        toast.success(t('social.postPublished', 'Post published'));
      }

      fetchData();
    } catch (error: any) {
      toast.dismiss('publishing');
      console.error('Error publishing post:', error);
      const msg = error?.response?.data?.message || error?.message || t('errors.generic', 'Error publishing post');
      toast.error(msg, { duration: 6000 });
    }
  };

  const openEditModal = (post: SocialPost) => {
    setEditingPost(post);
    setShowComposer(true);
  };

  // Boost requires a CONNECTED facebook_ads account (scopes ads_management/ads_read).
  // Hiding the rocket icon until that's true avoids the usual "I clicked boost and
  // got a permission error" moment — the modal used to assume ads creds existed.
  const hasFacebookAds = React.useMemo(
    () => accounts.some(a => a.platform === 'facebook_ads' && a.connected),
    [accounts]
  );

  // Retry only failed platforms. Preserves external_ids of already-published
  // ones, so IG/Twitter/LinkedIn that worked don't get duplicated when user
  // clicks retry on a 'partial' post.
  const handleRetryPlatforms = async (postId: string, platforms: string[]) => {
    try {
      toast.loading(t('social.retrying', 'Retrying...'), { id: 'retry-post' });
      const response = await socialService.retryPost(postId, platforms);
      toast.dismiss('retry-post');
      if (!response.success) {
        toast.error(response.message || t('errors.generic', 'Error retrying'));
        return;
      }
      toast.success(t('social.retryQueued', 'Retrying: {{list}}', { list: platforms.join(', ') }));
      // The retry enqueues a BullMQ job; refresh the list so the status flips
      // to 'processing', then refresh again after a short delay to catch the
      // post-publish state.
      fetchData();
      setTimeout(() => fetchData(), 5000);
    } catch (error: any) {
      toast.dismiss('retry-post');
      console.error('Retry platforms error:', error);
      toast.error(error?.response?.data?.message || error?.message || t('errors.generic', 'Error retrying'));
    }
  };

  // Account handlers
  const handleConnectAccount = async (platform: string) => {
    try {
      const response = await socialService.getOAuthUrl(platform, currentProject?.id);
      if (response.success && response.authUrl) {
        window.location.href = response.authUrl;
      } else {
        toast.error(response.message || t('errors.oauthNotConfigured', 'OAuth not configured for this platform'));
      }
    } catch (error: any) {
      console.error('Error initiating OAuth:', error);
      toast.error(error.response?.data?.message || t('errors.generic', 'Error connecting account'));
    }
  };

  const handleDisconnectAccount = async (accountId: string) => {
    if (!confirm(t('social.confirmDisconnect', 'Are you sure you want to disconnect this account?'))) return;
    try {
      await socialService.disconnectAccount(accountId);
      toast.success(t('social.accountDisconnected', 'Account disconnected'));
      fetchData();
    } catch (error) {
      console.error('Error disconnecting account:', error);
      toast.error(t('errors.generic', 'Error disconnecting account'));
    }
  };

  const handleRefreshAccount = async (accountId: string) => {
    try {
      await socialService.syncAccount(accountId);
      toast.success(t('social.accountSynced', 'Account synced'));
      fetchData();
    } catch (error) {
      console.error('Error syncing account:', error);
      toast.error(t('errors.generic', 'Error syncing account'));
    }
  };

  // Ref for cancelling generation
  const generationCancelledRef = useRef(false);

  // AI Suggestions handlers
  const handleGenerateSuggestions = async (
    customPrompt?: string,
    requestedCount?: number,
    platforms?: string[],
    opts?: { ignorePattern?: boolean; variationDirection?: string; generateImages?: boolean; referenceImageUrl?: string; tone?: string; imageStyle?: string; aspectRatio?: string; mentions?: string[] }
  ) => {
    // Check if platforms were selected
    if (!platforms || platforms.length === 0) {
      toast.error(t('social.selectPlatforms', 'Select at least one social network to generate suggestions'));
      return;
    }

    // Use the requested count or default to 4
    const count = requestedCount !== undefined && requestedCount !== null ? requestedCount : 4;

    generationCancelledRef.current = false;
    setIsGeneratingSuggestions(true);

    try {
      // Import polling service
      const { pollJobUntilComplete } = await import('../../services/aiJobService');

      const response = await socialService.generateSuggestions({
        count: count,
        platforms: platforms,
        topics: customPrompt ? [customPrompt] : undefined,
        async: true, // Use background processing
        ignorePattern: opts?.ignorePattern,
        variationDirection: opts?.variationDirection,
        generateImages: opts?.generateImages,
        referenceImageUrl: opts?.referenceImageUrl,
        tone: opts?.tone,
        imageStyle: opts?.imageStyle,
        aspectRatio: opts?.aspectRatio,
        mentions: opts?.mentions
      });

      // Check if cancelled
      if (generationCancelledRef.current) {
        return;
      }

      // If async mode, poll for result
      if (response.async && response.jobId) {
        toast.loading(t('social.generatingInBackground', 'Generating suggestions in background...'), { id: 'generating' });

        let result: any = null;
        let pollError: any = null;
        try {
          result = await pollJobUntilComplete(
            response.jobId,
            (progress, status) => {
              if (generationCancelledRef.current) return;
              console.log(`Generation progress: ${progress}% - ${status}`);
            },
            2000,
            // Carousel + multiple images can take several minutes. Give plenty
            // of room — user can always hit Cancel if they want to stop waiting.
            600000 // 10 min
          );
        } catch (err) {
          pollError = err;
        }

        toast.dismiss('generating');

        if (generationCancelledRef.current) {
          return;
        }

        if (result && Array.isArray(result)) {
          setSuggestions(prev => [...result, ...prev]);
          toast.success(t('social.suggestionsGenerated', 'Suggestions generated successfully!'));
        } else if (result?.data && Array.isArray(result.data)) {
          setSuggestions(prev => [...result.data, ...prev]);
          toast.success(t('social.suggestionsGenerated', 'Suggestions generated successfully!'));
        } else if (pollError) {
          // Polling failed (timeout, 404 after BullMQ cleanup, transient errors).
          // The worker still saved suggestions directly to the DB — refetch
          // before declaring failure so we don't throw away real work.
          try {
            const fresh = await socialService.getSuggestions();
            if (fresh.success && Array.isArray(fresh.data)) {
              setSuggestions(fresh.data);
              toast.success(t('social.suggestionsGenerated', 'Suggestions generated successfully!'));
            } else {
              throw pollError;
            }
          } catch {
            console.error('Error generating suggestions:', pollError);
            toast.error(pollError.message || t('errors.generic', 'Error generating suggestions'));
          }
        } else {
          toast.error(t('errors.generic', 'Error processing suggestions'));
        }
      } else if (response.success && response.data) {
        // Sync mode fallback
        setSuggestions(prev => [...response.data, ...prev]);
        toast.success(t('social.suggestionsGenerated', 'Suggestions generated successfully!'));
      } else {
        toast.error(t('errors.generic', 'Error generating suggestions'));
      }
    } catch (error: any) {
      toast.dismiss('generating');
      if (generationCancelledRef.current) {
        return;
      }
      console.error('Error generating suggestions:', error);
      toast.error(error.message || t('errors.generic', 'Error generating suggestions'));
    } finally {
      setIsGeneratingSuggestions(false);
      // Silent re-fetch to reconcile with the DB — kills the race where the
      // polled result and local state get out of sync.
      if (!generationCancelledRef.current) {
        socialService.getSuggestions()
          .then(res => {
            if (res.success && Array.isArray(res.data)) {
              setSuggestions(res.data);
            }
          })
          .catch(() => { /* keep whatever is in state */ });
      }
    }
  };

  const handleCancelGeneration = () => {
    generationCancelledRef.current = true;
    setIsGeneratingSuggestions(false);
    toast.success(t('social.generationCancelled', 'Generation cancelled'));
  };

  const handleRegenerateSuggestion = async (suggestion: AISuggestion) => {
    try {
      toast.loading(t('social.regenerating', 'Regenerating...'), { id: 'regenerating' });
      const response = await socialService.regenerateSuggestion(suggestion.id);
      if (response.success && response.data) {
        setSuggestions(prev => prev.map(s => s.id === suggestion.id ? response.data : s));
        toast.success(t('social.suggestionRegenerated', 'Regenerated'), { id: 'regenerating' });
      }
    } catch (error) {
      console.error('Error regenerating suggestion:', error);
      toast.error(t('errors.generic', 'Error'), { id: 'regenerating' });
    }
  };

  const handleDiscardSuggestion = async (id: string) => {
    try {
      await socialService.discardSuggestion(id);
      setSuggestions(prev => prev.filter(s => s.id !== id));
      toast.success(t('social.suggestionDiscarded', 'Discarded'));
    } catch (error) {
      console.error('Error discarding suggestion:', error);
      toast.error(t('errors.generic', 'Error'));
    }
  };

  // Fetch external posts when Published tab is selected
  const handleFetchExternalPosts = async () => {
    await fetchExternalPosts();
  };

  // Filter posts
  const filteredPosts = posts.filter(p => {
    if (statusFilter !== 'all' && p.status !== statusFilter) return false;
    if (searchQuery) {
      return p.content.toLowerCase().includes(searchQuery.toLowerCase());
    }
    return true;
  });

  const connectedAccountsCount = accounts.filter(a => a.connected).length;

  // Platforms that are ads, not social media
  const ADS_PLATFORMS = ['google_ads', 'facebook_ads', 'linkedin_ads'];

  // Render Published Posts with Metrics (fetched directly from social networks)
  const renderPublishedPosts = () => {
    // Filter out ads platforms - only show social media accounts
    const connectedAccounts = accounts.filter(a => a.connected && !ADS_PLATFORMS.includes(a.platform));

    // Filter posts by selected account
    const filteredExternalPosts = selectedAccountFilter
      ? externalPosts.filter(post => post.account_id === selectedAccountFilter)
      : externalPosts;

    return (
      <div className="space-y-4">
        {/* Header with refresh button */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              {t('socialMetrics.published', 'Published')}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t('socialMetrics.publishedDescription', 'Track the performance of your published posts')}
            </p>
          </div>
          <Button
            onClick={handleFetchExternalPosts}
            disabled={isFetchingExternal}
            variant="secondary"
            className="gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${isFetchingExternal ? 'animate-spin' : ''}`} />
            {isFetchingExternal ? t('socialMetrics.loading', 'Loading...') : t('socialMetrics.refresh', 'Refresh')}
          </Button>
        </div>

        {/* Connected accounts filter - clickable */}
        {connectedAccounts.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              {t('socialMetrics.filterByAccount', 'Filter by account')}:
            </p>
            <div className="flex flex-wrap gap-2">
              {/* All accounts button */}
              <button
                onClick={() => setSelectedAccountFilter(null)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-all ${
                  selectedAccountFilter === null
                    ? 'bg-primary-100 dark:bg-primary-900/30 border-primary-500 text-primary-700 dark:text-primary-300'
                    : 'bg-gray-50 dark:bg-gray-900/50 border-gray-200 dark:border-gray-600 hover:border-primary-300 dark:hover:border-primary-600'
                }`}
              >
                <span className="text-sm font-medium">
                  {t('common.all', 'All')}
                </span>
                <span className="text-xs px-1.5 py-0.5 bg-gray-200 dark:bg-gray-700 rounded">
                  {externalPosts.length}
                </span>
              </button>

              {/* Individual account buttons */}
              {connectedAccounts.map(acc => {
                const postCount = externalPosts.filter(p => p.account_id === acc.id).length;
                const isSelected = selectedAccountFilter === acc.id;

                return (
                  <button
                    key={acc.id}
                    onClick={() => setSelectedAccountFilter(isSelected ? null : acc.id)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-all ${
                      isSelected
                        ? 'bg-primary-100 dark:bg-primary-900/30 border-primary-500 text-primary-700 dark:text-primary-300'
                        : 'bg-gray-50 dark:bg-gray-900/50 border-gray-200 dark:border-gray-600 hover:border-primary-300 dark:hover:border-primary-600'
                    }`}
                  >
                    {acc.profile_image ? (
                      <img
                        src={acc.profile_image}
                        alt=""
                        className="w-6 h-6 rounded-full object-cover"
                        onError={(e) => {
                          // Fallback if image fails to load
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    ) : (
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold ${
                        acc.platform === 'instagram' ? 'bg-gradient-to-br from-purple-500 to-pink-500' :
                        acc.platform === 'facebook' ? 'bg-blue-600' :
                        acc.platform === 'linkedin' ? 'bg-blue-700' :
                        acc.platform === 'youtube' ? 'bg-red-600' :
                        acc.platform === 'tiktok' ? 'bg-black' :
                        acc.platform === 'twitter' ? 'bg-sky-500' :
                        acc.platform === 'threads' ? 'bg-black' :
                        'bg-gray-500'
                      }`}>
                        {acc.platform.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="text-left">
                      <span className="text-sm font-medium block">
                        {acc.username}
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                        {acc.platform}
                      </span>
                    </div>
                    {postCount > 0 && (
                      <span className={`text-xs px-1.5 py-0.5 rounded ${
                        isSelected
                          ? 'bg-primary-200 dark:bg-primary-800'
                          : 'bg-gray-200 dark:bg-gray-700'
                      }`}>
                        {postCount}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Fetch results - account status badges */}
        {externalAccountsInfo.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {externalAccountsInfo.map(acc => (
              <div
                key={acc.id}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm ${
                  acc.error
                    ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                    : 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                }`}
              >
                {acc.profile_image && (
                  <img src={acc.profile_image} alt="" className="w-5 h-5 rounded-full" />
                )}
                <span className="font-medium">{acc.username}</span>
                <span className="text-xs opacity-75">
                  {acc.error ? t('errors.fetchFailed', 'Error') : `${acc.posts_fetched} posts`}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Posts Grid */}
        {isFetchingExternal ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="h-80 bg-gray-200 dark:bg-gray-700 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : externalPosts.length === 0 ? (
          <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
            <TrendingUp className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              {connectedAccounts.length === 0
                ? t('socialMetrics.noAccounts', 'No accounts connected')
                : t('socialMetrics.noPublishedPosts', 'No posts found')}
            </h3>
            <p className="text-gray-500 dark:text-gray-400 mb-4">
              {connectedAccounts.length === 0
                ? t('socialMetrics.connectFirst', 'Connect your social networks to see your posts')
                : t('socialMetrics.clickToFetch', 'Click the button below to fetch your posts')}
            </p>
            {connectedAccounts.length === 0 ? (
              <Button onClick={() => setActiveTab('accounts')} className="gap-2">
                <Users className="h-4 w-4" />
                {t('social.tabs.accounts', 'Accounts')}
              </Button>
            ) : (
              <Button onClick={handleFetchExternalPosts} className="gap-2">
                <RefreshCw className="h-4 w-4" />
                {t('socialMetrics.fetchPosts', 'Fetch Posts')}
              </Button>
            )}
          </div>
        ) : filteredExternalPosts.length === 0 ? (
          <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
            <TrendingUp className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              {t('socialMetrics.noPostsForAccount', 'No posts found for this account')}
            </h3>
            <p className="text-gray-500 dark:text-gray-400 mb-4">
              {t('socialMetrics.tryRefresh', 'Try refreshing or select another account')}
            </p>
            <Button onClick={() => setSelectedAccountFilter(null)} variant="secondary" className="gap-2">
              {t('socialMetrics.showAll', 'Show all')}
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredExternalPosts.map(post => (
              <PostMetricsCard
                key={post.id}
                post={{
                  id: post.id,
                  content: post.content,
                  platforms: [post.platform],
                  published_at: post.published_at,
                  media_urls: post.media_urls,
                  external_ids: post.external_ids,
                  metrics: post.metrics,
                  permalink: post.permalink,
                  account_username: post.account_username,
                  account_profile_image: post.account_profile_image,
                  content_type: post.content_type
                }}
                onBoost={() => {
                  // Create a Content-like object for the boost modal
                  setBoostingPost({
                    id: post.id,
                    user_id: '',
                    content: post.content,
                    media_urls: post.media_urls || [],
                    platforms: [post.platform],
                    hashtags: [],
                    status: 'published',
                    published_at: post.published_at,
                    external_ids: post.external_ids,
                    ai_generated: false,
                    created_at: post.published_at,
                    metrics: post.metrics
                  } as SocialPost);
                  setShowBoostModal(true);
                }}
              />
            ))}
          </div>
        )}
      </div>
    );
  };

  // Render Dashboard
  const renderDashboard = () => (
    <div className="space-y-6">
      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3 md:gap-4">
        <QuickStatCard
          icon={<Users className="w-5 h-5 text-primary-600" />}
          label={t('social.followers', 'Followers')}
          value={socialStats.totalFollowers}
          change={socialStats.followersGrowth}
          color="bg-primary-100 dark:bg-primary-900/30"
        />
        <QuickStatCard
          icon={<FileText className="w-5 h-5 text-blue-600" />}
          label={t('social.posts', 'Posts')}
          value={socialStats.totalPosts}
          color="bg-blue-100 dark:bg-blue-900/30"
        />
        <QuickStatCard
          icon={<Clock className="w-5 h-5 text-purple-600" />}
          label={t('social.scheduled', 'Scheduled')}
          value={socialStats.scheduledPosts}
          color="bg-purple-100 dark:bg-purple-900/30"
        />
        <QuickStatCard
          icon={<Heart className="w-5 h-5 text-pink-600" />}
          label={t('social.engagement', 'Engagement')}
          value={`${socialStats.avgEngagement}%`}
          color="bg-pink-100 dark:bg-pink-900/30"
        />
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <button
          onClick={() => setShowComposer(true)}
          className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl p-4 sm:p-5 text-left text-white hover:from-indigo-600 hover:to-purple-700 transition-all"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-lg">
              <Plus className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-semibold">{t('social.newPost', 'New Post')}</h3>
              <p className="text-white/80 text-sm">{t('contentHub.createDescription', 'Create a post')}</p>
            </div>
          </div>
        </button>

        <button
          onClick={() => setActiveTab('suggestions')}
          className="bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl p-4 sm:p-5 text-left text-white hover:from-purple-600 hover:to-pink-600 transition-all"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-lg">
              <Sparkles className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-semibold">{t('social.aiSuggestions', 'AI Suggestions')}</h3>
              <p className="text-white/80 text-sm">{suggestions.length} {t('social.available', 'available')}</p>
            </div>
          </div>
        </button>

        <button
          onClick={() => setActiveTab('accounts')}
          className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-5 text-left border border-gray-200 dark:border-gray-700 hover:border-primary-300 hover:shadow-md transition-all"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
              <Share2 className="w-5 h-5 text-gray-600 dark:text-gray-300" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white">{t('social.connectedAccounts', 'Accounts')}</h3>
              <p className="text-gray-500 text-sm">{connectedAccountsCount} {t('social.connected', 'connected')}</p>
            </div>
          </div>
        </button>
      </div>

      {/* Recent Posts */}
      {posts.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <h3 className="font-semibold text-gray-900 dark:text-white">
              {t('contentHub.recentContent', 'Recent Posts')}
            </h3>
            <button
              onClick={() => setActiveTab('posts')}
              className="text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              {t('common.viewAll', 'View all')}
            </button>
          </div>
          <div className="p-4 space-y-3">
            {posts.slice(0, 3).map(post => (
              <PostCard
                key={post.id}
                post={post}
                onEdit={() => openEditModal(post)}
                onDelete={() => handleDeletePost(post.id)}
                onPublish={() => handlePublishPost(post.id)}
                onBoost={() => { setBoostingPost(post); setShowBoostModal(true); }}
                canBoost={hasFacebookAds}
                onRetryPlatforms={(plats) => handleRetryPlatforms(post.id, plats)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );

  // Render Posts List
  const renderPosts = () => (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder={t('common.search', 'Search...')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
        <div className="flex gap-2">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
          >
            <option value="all">{t('common.allStatus', 'All')}</option>
            <option value="draft">{t('social.status.draft', 'Draft')}</option>
            <option value="scheduled">{t('social.status.scheduled', 'Scheduled')}</option>
            <option value="published">{t('social.status.published', 'Published')}</option>
          </select>
          <Button onClick={() => setShowComposer(true)} className="gap-2 whitespace-nowrap">
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">{t('social.newPost', 'New Post')}</span>
            <span className="sm:hidden">New</span>
          </Button>
        </div>
      </div>

      {/* Posts List */}
      {isLoading ? (
        <div className="space-y-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-24 bg-gray-200 dark:bg-gray-700 rounded-xl animate-pulse" />
          ))}
        </div>
      ) : filteredPosts.length === 0 ? (
        <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
          <FileText className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            {t('social.noPosts', 'No posts')}
          </h3>
          <p className="text-gray-500 dark:text-gray-400 mb-4">
            {t('social.createFirstPost', 'Create your first post')}
          </p>
          <Button onClick={() => setShowComposer(true)} className="gap-2">
            <Plus className="h-4 w-4" />
            {t('social.newPost', 'New Post')}
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredPosts.map(post => (
            <PostCard
              key={post.id}
              post={post}
              onEdit={() => openEditModal(post)}
              onDelete={() => handleDeletePost(post.id)}
              onPublish={() => handlePublishPost(post.id)}
              onBoost={() => { setBoostingPost(post); setShowBoostModal(true); }}
              canBoost={hasFacebookAds}
              onRetryPlatforms={(plats) => handleRetryPlatforms(post.id, plats)}
            />
          ))}
        </div>
      )}
    </div>
  );

  const currentTab = tabs.find(t => t.id === activeTab);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 -mx-4 sm:-mx-6 lg:-mx-8 -mb-4 sm:-mb-6 lg:-mb-8">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-y border-gray-200 dark:border-gray-700 lg:sticky lg:top-16 z-10">
        <div className="px-4 sm:px-6">
          {/* Title Row */}
          <div className="flex items-center justify-between gap-3 py-3 sm:py-4">
            <div className="flex items-center gap-3 min-w-0">
              <div className="p-2 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl">
                <Share2 className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">
                  {t('nav.socialMedia', 'Social Media')}
                </h1>
                <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                  {t('contentHub.subtitle', 'Manage your posts')}
                </p>
              </div>
            </div>
            <Button variant="primary" onClick={() => setShowComposer(true)} className="gap-1.5 whitespace-nowrap flex-shrink-0" aria-label={t('social.newPost', 'New Post')}>
              <Plus className="w-4 h-4" />
              <span className="sm:hidden">{t('social.newPostShort', 'Criar')}</span>
              <span className="hidden sm:inline">{t('social.newPost', 'New Post')}</span>
            </Button>
          </div>

          {/* Abas da área de redes sociais: uma linha só, rola de lado no celular */}
          <nav
            aria-label={t('nav.socialMedia', 'Redes sociais')}
            className="flex gap-1 overflow-x-auto overscroll-x-contain -mb-px [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  ref={isActive ? activeTabRef : undefined}
                  onClick={() => setActiveTab(tab.id)}
                  aria-current={isActive ? 'page' : undefined}
                  className={`shrink-0 flex items-center gap-2 px-3 sm:px-4 py-3 text-sm whitespace-nowrap border-b-2 transition-colors ${
                    isActive
                      ? 'border-primary-500 text-primary-600 dark:text-primary-400 font-semibold'
                      : 'border-transparent text-gray-500 dark:text-gray-400 font-medium hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
                  }`}
                >
                  <Icon className={`h-4 w-4 ${tab.highlight ? 'text-purple-500' : ''}`} />
                  {tab.label}
                  {tab.highlight && (
                    <span className="px-1.5 py-0.5 text-[10px] font-bold bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded">
                      IA
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 sm:px-6 py-6">
        {isLoading && activeTab === 'dashboard' ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-6 h-6 animate-spin text-primary-600" />
          </div>
        ) : (
          <>
            {activeTab === 'dashboard' && renderDashboard()}

            {activeTab === 'suggestions' && (
              <AISuggestions
                suggestions={suggestions}
                loading={isLoading}
                generating={isGeneratingSuggestions}
                connectedAccounts={accounts.map(a => ({
                  id: a.id,
                  platform: a.platform,
                  username: a.username,
                  connected: a.connected
                }))}
                onSchedule={(suggestion) => {
                  const suggestedPlatforms = suggestion.suggested_platforms || suggestion.platforms;
                  const meta = suggestion.metadata || {};
                  const mediaUrls = (Array.isArray(suggestion.media_urls) && suggestion.media_urls.length > 0)
                    ? suggestion.media_urls
                    : (suggestion.image_url ? [suggestion.image_url] : []);
                  setEditingPost({
                    id: suggestion.id,
                    user_id: '',
                    content: suggestion.content,
                    platforms: suggestedPlatforms,
                    media_urls: mediaUrls,
                    hashtags: suggestion.hashtags || [],
                    status: 'draft',
                    scheduled_at: suggestion.suggested_time,
                    ai_generated: true,
                    first_comment: (suggestion as any).first_comment || meta.instagram?.first_comment || '',
                    metadata: meta,
                    created_at: new Date().toISOString()
                  });
                  setShowComposer(true);
                }}
                onEdit={(suggestion) => {
                  const suggestedPlatforms = suggestion.suggested_platforms || suggestion.platforms;
                  const meta = suggestion.metadata || {};
                  const mediaUrls = (Array.isArray(suggestion.media_urls) && suggestion.media_urls.length > 0)
                    ? suggestion.media_urls
                    : (suggestion.image_url ? [suggestion.image_url] : []);
                  setEditingPost({
                    id: '',
                    user_id: '',
                    content: suggestion.content,
                    platforms: suggestedPlatforms,
                    media_urls: mediaUrls,
                    hashtags: suggestion.hashtags || [],
                    status: 'draft',
                    ai_generated: true,
                    first_comment: (suggestion as any).first_comment || meta.instagram?.first_comment || '',
                    metadata: meta,
                    created_at: new Date().toISOString()
                  });
                  setShowComposer(true);
                }}
                onRegenerate={handleRegenerateSuggestion}
                onDiscard={handleDiscardSuggestion}
                onPublishNow={async (suggestion) => {
                  if (!window.confirm(t('social.confirmPublishNow', 'Publicar esta sugestão agora? Ela será postada imediatamente nas redes selecionadas.'))) return;
                  await handlePublishPost(suggestion.id);
                  // já não é mais "suggested" — remove da lista de sugestões
                  setSuggestions(prev => prev.filter(s => s.id !== suggestion.id));
                }}
                onGenerateMore={handleGenerateSuggestions}
                onCancelGeneration={handleCancelGeneration}
              />
            )}

            {activeTab === 'posts' && renderPosts()}

            {activeTab === 'published' && renderPublishedPosts()}

            {activeTab === 'calendar' && (
              <ContentCalendar
                // Inclui `failed` e `partial` pra posts que tentaram publicar
                // não sumirem do calendário — user precisa ver pra retentar.
                posts={posts.filter(p => ['scheduled', 'published', 'draft', 'failed', 'partial'].includes(p.status)).map(p => ({
                  id: p.id,
                  content: p.content,
                  platforms: p.platforms,
                  scheduled_at: p.scheduled_at || p.created_at,
                  status: p.status as 'draft' | 'scheduled' | 'published' | 'failed' | 'partial',
                  media_url: p.media_urls?.[0]
                }))}
                loading={isLoading}
                onPostClick={(post) => {
                  const fullPost = posts.find(p => p.id === post.id);
                  if (fullPost) openEditModal(fullPost);
                }}
                onEditPost={(post) => {
                  const fullPost = posts.find(p => p.id === post.id);
                  if (fullPost) openEditModal(fullPost);
                }}
                onDeletePost={handleDeletePost}
                onCreatePost={() => {
                  setEditingPost(null);
                  setShowComposer(true);
                }}
              />
            )}

            {activeTab === 'analytics' && (
              <SocialAnalytics
                loading={isLoading}
                accounts={accounts}
                posts={posts.filter(p => p.status === 'published')}
                analyticsData={analyticsData}
              />
            )}

            {activeTab === 'listening' && (
              <SocialListening loading={isLoading} />
            )}

            {activeTab === 'threads_replies' && (
              <ThreadsReplies
                hasAnyConnectedSocial={accounts.some(a => ['threads', 'instagram', 'facebook'].includes(a.platform) && a.connected)}
              />
            )}

            {activeTab === 'accounts' && (
              <AccountsManager
                accounts={accounts.map(a => ({
                  id: a.id,
                  platform: a.platform,
                  account_name: a.username,
                  username: `@${a.username}`,
                  profile_image: a.profile_image,
                  followers: a.followers || 0,
                  following: a.following,
                  posts_count: a.posts_count,
                  is_connected: a.connected,
                  last_sync: a.last_sync,
                  token_expires: a.token_expires_at,
                  permissions: []
                }))}
                loading={isLoading}
                onConnect={handleConnectAccount}
                onDisconnect={handleDisconnectAccount}
                onRefresh={handleRefreshAccount}
              />
            )}
          </>
        )}
      </div>

      {/* Advanced Composer Modal */}
      <AdvancedComposer
        isOpen={showComposer}
        onClose={() => {
          setShowComposer(false);
          setEditingPost(null);
        }}
        onSubmit={handleCreatePost}
        editingPost={editingPost ? {
          content: editingPost.content,
          platforms: editingPost.platforms,
          scheduled_at: editingPost.scheduled_at,
          media_urls: editingPost.media_urls,
          hashtags: editingPost.hashtags || [],
          status: (editingPost.status === 'scheduled' ? 'scheduled' : 'draft') as 'draft' | 'scheduled',
          metadata: editingPost.metadata
        } : null}
        connectedAccounts={accounts.map(a => ({
          id: a.id,
          platform: a.platform,
          username: a.username,
          profile_image: a.profile_image || null,
          connected: a.connected,
        }))}
        isSubmitting={isSubmitting}
      />

      {/* Boost Post Modal */}
      <BoostPostModal
        isOpen={showBoostModal}
        onClose={() => { setShowBoostModal(false); setBoostingPost(null); }}
        post={boostingPost}
        onBoostSuccess={() => fetchData()}
      />
    </div>
  );
};

export default ContentHubPage;
